// controllers/patrol_controller.js
import { Controller } from "@hotwired/stimulus"
import { I18n } from 'i18n-js'
import locales from '../locales.json'
import PatrolDatabaseService from '../services/patrol_database_service'
import LocationService from '../services/location_service'
import PatrolState from '../services/patrol_state'
import PatrolBigbutton from '../presenters/patrol_bigbutton'
import PatrolInterface from '../presenters/patrol_interface'
import MediaStorageService from "../services/media_storage_service"
import { Modal } from "bootstrap"
import { DirectUpload } from "@rails/activestorage";
import { get } from "@rails/request.js"
import {
  security_services_building_complex_patrols_road_path,
  record_security_services_building_complex_patrols_road_path,
  security_services_building_complex_patrols_road_patrols_path,
  postmortem_security_services_building_complex_patrols_road_patrol_path,
  rails_direct_uploads_path,
  security_services_building_complex_patrols_roads_path
} from "../routes"
import Sqids from 'sqids';

export default class PatrolController extends Controller {
  static values = {
    buildingComplexId: Number,
    roadId: Number,
    patrolId: Number,
    trackingInterval: { type: Number, default: 10000 },
  }
  static targets = ["bigButton", "backButton", "stopButton", "mediaButton", "totalTime", "endTime",
    "uploadProgress", "currentFileProgress", "uploadStatus", "recordingModal"
  ]

  initialize() {
    // Only initialize things that don't require DOM elements
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']").content
    
    // Initialize services that don't require DOM
    this.dbService = new PatrolDatabaseService()
    this.patrolState = new PatrolState()
    this.mediaStorage = new MediaStorageService()
    this.sqids = new Sqids({minLength: 10})

    this.messageEventListeners = [];
  }


  async disconnect() {
    if (window.ReactNativeWebView) {
      this.messageEventListeners.forEach(listener => {
        document.removeEventListener("message", listener);
        window.removeEventListener("message", listener);
      });
    }
    if (this.locationService) {
      await this.locationService.stopTracking()
    }
    this.patrolState.reset()
  }

  async connect() {
    if (document.documentElement.hasAttribute('data-turbo-preview')) {
      return;
    }

    if (window.ReactNativeWebView) {
      const listener = this.handleMessage.bind(this);
      document.addEventListener("message", listener);
      window.addEventListener("message", listener);
      this.messageEventListeners.push(listener);
    }
    if (this.hasBigButtonTarget) {
      this.bigbutton = new PatrolBigbutton(this.bigButtonTarget, this.i18n)
      this.interface = new PatrolInterface({ mediaButton: this.mediaButtonTarget, backButton: this.backButtonTarget, stopButton: this.stopButtonTarget, uploadStatus: this.uploadStatusTarget }, this.i18n)
      this.locationService = new LocationService(
        this.handlePositionUpdate.bind(this),
        this.handlePositionError.bind(this)
      )

      try {
        this.bigbutton.showLoading()
        await this.dbService.initDB()
        const dataLoaded = await this.loadData()
        
        if (!dataLoaded) {
          throw new Error(this.i18n.t('security_services.patrols.errors.data_load_failed'))
        }
        
        if (!window.ReactNativeWebView) {
          if (await this.locationService.requestDevicePermissions()) {
            this.patrolState.permissionsGranted = true
            this.bigbutton.showStartButton()
          
            await this.mediaStorage.initDB()
          }
        } else {
          this.bigbutton.showStartButton()
          
          await this.mediaStorage.initDB()
        }
      } catch (error) {
        console.error("Initialization error:", error)
        this.bigbutton?.showError?.(error.message)
      }
    }
  }


  async loadData() {
    try {
      await this.dbService.deleteDB()
      const response = await this.fetchPatrolData()
      
      if (response.ok) {
        const data = await response.json
        this.updateUIWithPatrolData(data)
        await this.dbService.storePatrolData(data)
        if (data.checkpoints) {
          this.patrolState.initializeCheckpoints(data.checkpoints)
        }
        return true
      } else {
        throw new Error(`Failed to fetch patrol data: ${response.status}`)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      throw error
    }
  }

  async fetchPatrolData() {
    return await get(
      security_services_building_complex_patrols_road_path(
        this.buildingComplexIdValue, 
        this.roadIdValue
      ), {
        responseKind: "json",
        contentType: "application/json"
      }
    )
  }

  updateUIWithPatrolData(data) {
    if (this.hasTotalTimeTarget) {
      this.totalTimeTarget.innerHTML = `${data.total_time} ${this.i18n.t("helpers.minutes")}`
    }
    if (this.hasEndTimeTarget) {
      this.endTimeTarget.innerHTML = this.i18n.l("time.formats.short", data.end_time)
    }
  }

  async handleButton(event = null) {
    if (this.patrolState.isFirstCheckpoint) {
      if (!this.patrolState.permissionsGranted && !window.ReactNativeWebView) {
        console.error('Required permissions not granted')
        return
      }
      await this.dbService.storePatrolStartTime(this.roadIdValue)
      this.interface.showMediaButton()
      this.interface.enableStopButton()
    } else if (this.patrolState.hasCurrentCheckpoint) {
      await this.dbService.storeCompletionTime(
        this.roadIdValue,
        this.patrolState.currentCheckpoint.id
      )
    }

    const checkpoint = await this.patrolState.nextCheckpoint()
    if (checkpoint) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'nfc_init' }))
      } else {
        if ('NDEFReader' in window) {
          const ndef = new NDEFReader();
          try {
            await ndef.scan();
            ndef.onreading = event => {
              const decoder = new TextDecoder();
              
              for (const record of event.message.records) {
                let extractedText;
                
                // Check if it's a text record with language code
                if (record.recordType === "text") {
                  // Extract text properly handling the language code
                  const data = new Uint8Array(record.data.buffer);
                  // First byte contains language code length
                  const langLength = data[0];
                  // Skip language code bytes
                  extractedText = decoder.decode(data.slice(1 + langLength));
                } else {
                  // Fallback for other record types
                  extractedText = decoder.decode(record.data);
                }
                
                console.log("Extracted text from NFC tag:", extractedText);
                
                try {
                  // First try Sqids decoding (primary format)
                  const decodedIds = this.sqids.decode(extractedText.trim());
                  console.log("Sqids decoded payload:", decodedIds);
                  
                  if (decodedIds.length > 0 && decodedIds[0].toString() === checkpoint.id.toString()) {
                    console.log("NFC tag matches the checkpoint ID!");
                    this.handleButton();
                    ndef.onreading = null; // Remove listener after reading
                    return; // Exit after successful match
                  }
                } catch (sqidsError) {
                  console.log("Sqids decoding failed, trying fallback:", sqidsError);
                }
                
                try {
                  // Try JSON parsing as fallback
                  let jsonData = extractedText;
                  
                  // Check if text is a partial JSON and try to fix it
                  if (!extractedText.trim().startsWith('{')) {
                    jsonData = '{"' + extractedText.trim();
                    if (!jsonData.endsWith('}')) {
                      jsonData += '"}'; // Close the JSON
                    }
                  }
                  
                  const parsedData = JSON.parse(jsonData);
                  console.log("Parsed JSON data:", parsedData);
                  
                  // Check checkpoint from JSON
                  const checkpointId = parsedData.checkpoint || parsedData.id;
                  if (checkpointId && checkpointId.toString() === checkpoint.id.toString()) {
                    console.log("NFC tag JSON data matches the checkpoint ID!");
                    this.handleButton();
                    ndef.onreading = null; // Remove listener
                    return;
                  }
                } catch (jsonError) {
                  console.log("JSON parsing failed:", jsonError);
                }
                
                // Last resort - try to extract numbers from the string
                const numericMatches = extractedText.match(/\d+/);
                if (numericMatches && numericMatches.length > 0) {
                  const possibleCheckpoint = parseInt(numericMatches[0], 10);
                  if (possibleCheckpoint.toString() === checkpoint.id.toString()) {
                    console.log("Found matching checkpoint ID in text!");
                    this.handleButton();
                    ndef.onreading = null; // Remove listener
                    return;
                  }
                }
                
                console.log("NFC tag does not match the checkpoint ID");
              }
            };
          } catch (error) {
            console.error(`NFC read failed: ${error}`);
          }
        } else {
          alert('NFC is not supported on this device.');
        }
      }
      this.locationService.startTracking()
      this.bigbutton.updateCheckpoint(checkpoint)
      this.stopButtonTarget.disabled = false
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'change_checkpoint', checkpoint_id: checkpoint.id }))
      }
    } else {
      await this.finishPatrol()
    }
  }

  async finishPatrol() {
    this.interface.hideMediaButton()
    this.interface.disableStopButton()
    await this.locationService.stopTracking()
    this.stopButtonTarget.disabled = true
    await this.submitCompletionTimes()
  }

  async handleMessage(event) {
    console.log(event);
    let parsedMessage;

    try {
      try {
        parsedMessage = JSON.parse(event.data);
      } catch (error) {
        console.error('Error parsing JSON:', error);
        return;
      }

      if (parsedMessage && parsedMessage.action == 'finish_patrol') {
        let uploadedItems = parsedMessage.uploadedItems;
        let coordinates = parsedMessage.locations;

        const patrol = await this.dbService.getPatrol(this.roadIdValue)
    
        const payload = await this.prepareSubmissionPayload(patrol, uploadedItems, coordinates);
    
        const response = await this.submitPatrolData(payload);
    
        if (response.ok) {
          await this.handleSuccessfulSubmission(response);
        } else {
          this.bigbutton.showRetryButton();
        }
      }
      else if (parsedMessage && parsedMessage.action == 'approved_checkpoint') {
        console.log('Approved checkpoint:', parsedMessage.checkpoint_id);
        this.handleButton(); 
      }
      else if (parsedMessage && parsedMessage.action == 'nfc_write_end') {
        console.log('NFC write successful:', parsedMessage);
        this.enableLearnButton();
        // Alert or show a toast notification
        alert(`NFC chip tanítás sikeres!`);
      }
      else if (parsedMessage && parsedMessage.action == 'nfc_write_error') {
        console.error('NFC write error:', parsedMessage);
        this.enableLearnButton();
        
        // Show a user-friendly error message
        let errorMessage = 'NFC chip tanítás sikertelen';
        if (parsedMessage.data && parsedMessage.data.i18n && parsedMessage.data.i18n.message) {
          errorMessage = parsedMessage.data.i18n.message;
        } else if (parsedMessage.data && parsedMessage.data.error) {
          errorMessage = parsedMessage.data.error;
        }
        
        alert(errorMessage);
      }
      else if (parsedMessage && parsedMessage.action == 'get_csrf_token') {
        const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'csrftoken', token: csrfToken }));
      }
    } catch (error) {
      console.error('Error submitting completion times:', error)
      this.bigbutton.showRetryButton()
    }


  }

  async submitCompletionTimes() {
    this.bigbutton.showSubmitting()

    try {

      if (window.ReactNativeWebView) {
        const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'finish_patrol', csrf_token: csrfToken }))

      } else {
        const patrol = await this.dbService.getPatrol(this.roadIdValue)
        const mediaItems = await this.mediaStorage.getMediaByGroupId(this.roadIdValue)

        if (this.hasUploadStatusTarget) {
          this.uploadStatusTarget.classList.remove('d-none')
        }

        const uploadedMedia = await this.uploadAllMedia(mediaItems)

        if (this.hasUploadStatusTarget) {
          this.uploadStatusTarget.classList.add('d-none')
        }

        const coordinates = await this.dbService.getGpsCoordinates(this.roadIdValue)

        const payload = await this.prepareSubmissionPayload(patrol, uploadedMedia, coordinates)

        console.log(patrol)
        console.log(payload)
  
        const response = await this.submitPatrolData(payload)

        if (response.ok) {
          await this.handleSuccessfulSubmission(response)
        } else {
          this.bigbutton.showRetryButton()
        }
      }
    } catch (error) {
      console.error('Error submitting completion times:', error)
      this.bigbutton.showRetryButton()
    }
  }

  async prepareSubmissionPayload(patrol, uploadedMedia, coordinates) {
    const completionTimes = await this.dbService.getCompletionTimes(this.roadIdValue)
    const lastCompletionTime = await this.dbService.getLastCompletionTime(this.roadIdValue)

    return {
      security_services_patrols_patrol: {
        road_id: this.roadIdValue,
        start_time: patrol?.start_time,
        end_time: lastCompletionTime,
        status: "finished",
        checkpoints_attributes: completionTimes,
        coordinates_attributes: coordinates,
        media_attributes: uploadedMedia // Array of uploaded media with signed_ids
      }
    }
  }

  async handleSuccessfulSubmission(response) {
    const patrolData = await response.json()
    await this.mediaStorage.clearPatrolMedia(this.roadIdValue)
    await this.dbService.deleteDB()
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'cleanup' }))
    }
    
    await this.waitNetwork();
    Turbo.visit(postmortem_security_services_building_complex_patrols_road_patrol_path(
      this.buildingComplexIdValue,
      this.roadIdValue,
      patrolData.id
    ))
  }

  async handlePositionUpdate(position) {
    if (!this.locationService.isTracking) return
    
    const coordinates = {
      road_id: this.roadIdValue,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitude_accuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
      timestamp: new Date().toISOString(),
      checkpoint_id: this.patrolState.currentCheckpoint?.id || null
    }

    await this.dbService.storeGpsCoordinates(coordinates)
  }

  handlePositionError(error) {
    console.error('GPS Error:', error.message)
    // Additional error handling as needed
  }

  async submitPatrolData(payload) {
    await this.waitNetwork();

    try {
      const response = await fetch(
        security_services_building_complex_patrols_road_patrols_path(
          this.buildingComplexIdValue,
          this.roadIdValue
        ), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          },
          body: JSON.stringify(payload)
        }
      )
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
  
      return response
    } catch (error) {
      console.error('Error submitting patrol data:', error)
      throw error
    }
  }
  
  async uploadAllMedia(mediaItems) {
    const uploadedMedia = []
    this.totalFiles = mediaItems.length
    this.completedFiles = 0

    this.interface.updateUploadStatus()

    for (const mediaItem of mediaItems) {
      try {
        console.log(mediaItem)
        this.interface.updateCurrentFileProgress(0)
        // Convert the stored data to a File object
        const file = await this.createFileFromMediaItem(mediaItem)
        
        // Upload using DirectUpload
        const blob = await this.uploadWithDirectUpload(file)
        
        uploadedMedia.push({
          signed_id: blob.signed_id,
          filename: blob.filename,
          content_type: blob.content_type
        })
        this.completedFiles++
        this.interface.updateTotalProgress(this.completedFiles*100, this.totalFiles*100)
      } catch (error) {
        console.error(`Failed to upload media item: ${mediaItem.id}`, error)
        // Continue with other uploads even if one fails
      }
    }

    return uploadedMedia
  }

  async createFileFromMediaItem(mediaItem) {
    const filename = `${mediaItem.type}_${new Date(mediaItem.timestamp).getTime()}.${this.getFileExtension(mediaItem)}`
  
    return new File([mediaItem.blob], filename, {
      type: mediaItem.blob.type,
      lastModified: new Date(mediaItem.timestamp).getTime()
    })
  }

  async uploadWithDirectUpload(file) {
    await this.waitNetwork();
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(
        file,
        rails_direct_uploads_path(),
        this
      )

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob)
        }
      })
    })
  }

  getFileExtension(mediaItem) {
    if (mediaItem.type === 'image') {
      return 'jpg'
    } else if (mediaItem.type === 'video') {
      return 'webm'
    }
    throw new Error(`Unsupported media type: ${mediaItem.type}`)
  }

  // Progress tracking for DirectUpload
  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener('progress', event => {
      const progress = event.loaded / event.total * 100
      this.interface.updateCurrentFileProgress(progress)
      console.log(progress + this.completedFiles  * 100)
      this.interface.updateTotalProgress(progress + this.completedFiles  * 100, this.totalFiles*100)
      console.log(`Upload progress: ${progress}%`)
      // You could dispatch a custom event here for UI updates
      this.dispatch('uploadProgress', { 
        detail: { progress, total: event.total, loaded: event.loaded } 
      })
    })
  }

  showRecordingModal() {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'recording' }))
    } else {
      const modal = new Modal(this.recordingModalTarget)
      modal.show()
    }
  }

  async stopPatrol() {
    await this.finishPatrol()
    // if (!window.ReactNativeWebView) {
    //   await this.waitNetwork();
    //   Turbo.visit(security_services_building_complex_patrols_roads_path(this.buildingComplexIdValue))
    // }
  }
  
  async learn(event) {
    const metaTag = document.querySelector('meta[name="user_token"]');
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    
    // Show spinner on the button
    this.nfcLearnButton = event.target.closest('button')
    
    this.disableLearnButton()

    // Encode the checkpoint ID using Sqids - this is the standardized format for both web and mobile
    const encodedValue = this.sqids.encode([event.params.id]);
    console.log(`Encoding checkpoint ID ${event.params.id} as Sqids value: ${encodedValue}`);

    if (window.ReactNativeWebView) {
      // Only include user_token if the meta tag exists (user is logged in)
      window.ReactNativeWebView.postMessage(JSON.stringify({
        action: 'nfc_write',
        id: event.params.id,
        value: encodedValue,  // Always send the Sqids encoded value for compatibility
        csrf_token: csrfToken,
        ...(metaTag && { user_token: metaTag.content })
      }));
    } else {
      if ('NDEFReader' in window) {
        const ndef = new NDEFReader();
        try {
          // Create a proper NDEF text record with language prefix for better compatibility
          const encoder = new TextEncoder();
          // Format: [language code length][language code][actual text]
          const languageCode = 'en'; // Use 'en' as language code
          const languageBytes = encoder.encode(languageCode);
          
          // Create the properly formatted text record
          const textBytes = encoder.encode(encodedValue);
          const payload = new Uint8Array(1 + languageBytes.length + textBytes.length);
          
          // Set first byte to language length
          payload[0] = languageBytes.length;
          
          // Copy language bytes
          payload.set(languageBytes, 1);
          
          // Copy text bytes
          payload.set(textBytes, 1 + languageBytes.length);
          
          // Write the properly formatted NDEF record
          await ndef.write({
            records: [{
              recordType: "text",
              data: payload,
              lang: languageCode
            }]
          });
          
          alert(`NFC chip tanítás sikeres!`);
        } catch (error) {
          console.error(`NFC write failed: ${error}`);
          alert(`NFC chip tanítás sikertelen: ${error}`);
        } finally {
          // Revert button content back to original
          this.enableLearnButton();
        }
      } else {
        alert('NFC nem támogatott ezen az eszközön.');
      }
    }
  }

  disableLearnButton() {
    this.nfcLearnButton.disabled = true;
    console.log(this.nfcLearnButton.querySelector('.nfc-icon'))
    this.nfcLearnButton.querySelector('.nfc-icon').classList = 'spinner-border spinner-border-sm nfc-icon'
  }

  enableLearnButton() {
    this.nfcLearnButton.disabled = false;
    this.nfcLearnButton.querySelector('.nfc-icon').classList = 'bi bi-tag nfc-icon';
  }

  async goBack() {
    this.interface.hideMediaButton()
    this.interface.disableStopButton()
    // Stop location tracking before navigating back
    await this.locationService.stopTracking(true)
    
    // Wait 1 second to ensure the stop message is processed
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Use js-routes directly to navigate back to the roads list
    const path = security_services_building_complex_patrols_roads_path(this.buildingComplexIdValue)
    window.location.href = path
  }

  async waitNetwork() {
    let canWait = false;
    const testConnection = async () => {
      try {
        const response = await fetch('/up');
        console.log('Network response:', response);
        return response.ok;
      } catch (error) {
        console.log('Network error:', error);
        return false;
      }
    };

    while (!navigator.onLine || !(await testConnection())) {
      console.log('navigator.onLine:', navigator.onLine);
      console.log('Network connection:', await testConnection());
      canWait = true;
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'wait_network' }));
      }
      console.log('No internet connection. Waiting to retry...');
      await new Promise(resolve => setTimeout(resolve, 5000)); // Wait for 5 seconds before retrying
    }

    if (window.ReactNativeWebView && canWait) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'network_ready' }));
    }
  }

  // Handle mobile app patrol start navigation
  startPatrol(event) {
    event.preventDefault(); // Always prevent default since we handle all navigation
    
    const roadId = event.params.roadId;
    const buildingComplexId = event.params.buildingComplexId;
    
    console.log('Starting patrol for road:', roadId, 'in building complex:', buildingComplexId);
    
    if (window.ReactNativeWebView) {
      // Mobile app: send postMessage
      window.ReactNativeWebView.postMessage(JSON.stringify({
        action: 'NAVIGATE_TO_PATROL',
        data: {
          roadId: roadId,
          buildingComplexId: buildingComplexId
        }
      }));
      
      console.log('PostMessage sent to mobile app');
    } else {
      // Web browser: manual navigation using JavaScript
      const recordPath = record_security_services_building_complex_patrols_road_path(buildingComplexId, roadId);
      console.log('Web browser: navigating to', recordPath);
      window.location.href = recordPath;
    }
  }
}
